import { useRef } from 'react';
import { Button, Input, InputNumber, Space, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import RichTextEditor from '../../components/RichTextEditor';

const { Title, Text } = Typography;

export const DEFAULT_PAYROLL_CONSENT_HTML = `Aylık Maaş Kesintisi Onay Sözleşmesi

İşbu metin, {{monthLabel}} dönemine ait şarj kullanım bedelinin aylık maaştan kesilmesine ilişkin onaydır.

Çalışan adı soyadı: {{fullName}}
Sicil numarası: {{employeeId}}
Pozisyon: {{position}}
Kesinti tutarı: {{amount}}
Dönem: {{monthLabel}}

{{fullName}}, sicil numarası {{employeeId}}, {{position}} olarak görev yapmakta olup, {{monthLabel}} dönemine ait şarj kullanım bedeli olan {{amount}} tutarının maaşından kesilmesini kabul eder.

Bu onay, çalışanın mobil uygulamada ekrana çizdiği imza ile geçerlidir.`;

const TOKENS = [
  { labelKey: 'consentTokenName', token: '{{fullName}}' },
  { labelKey: 'consentTokenEmployeeId', token: '{{employeeId}}' },
  { labelKey: 'consentTokenPosition', token: '{{position}}' },
  { labelKey: 'consentTokenAmount', token: '{{amount}}' },
  { labelKey: 'consentTokenMonth', token: '{{monthLabel}}' },
];

const PayrollConsentTab = ({
  enabled,
  html,
  emails,
  windowDays,
  attachPdfs,
  canEdit,
  saving,
  onEnabledChange,
  onHtmlChange,
  onEmailsChange,
  onWindowDaysChange,
  onAttachPdfsChange,
  onSave,
}) => {
  const { t } = useTranslation('settings');
  const editorRef = useRef(null);

  const insertToken = (token) => {
    const editor = editorRef.current?.getEditor?.();
    if (!editor) {
      onHtmlChange(`${html || ''}${token}`);
      return;
    }
    const range = editor.getSelection(true);
    const index = range ? range.index : Math.max(editor.getLength() - 1, 0);
    editor.insertText(index, token, 'user');
    onHtmlChange(editor.root.innerHTML);
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Title level={4} className="!mb-1">
          {t('consentTabTitle')}
        </Title>
        <Text type="secondary">{t('consentTabDescription')}</Text>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={enabled} disabled={!canEdit} onChange={onEnabledChange} />
        <Text>{t('consentEnabledLabel')}</Text>
      </div>
      <Text type="secondary">{t('consentEnabledHelp')}</Text>
      <div>
        <Text strong>{t('consentTemplateLabel')}</Text>
        <div className="mt-2 mb-2">
          <Space wrap>
            {TOKENS.map((item) => (
              <Button
                key={item.token}
                size="small"
                disabled={!canEdit}
                onClick={() => insertToken(item.token)}
              >
                {t(item.labelKey)}
              </Button>
            ))}
          </Space>
        </div>
        <RichTextEditor
          ref={editorRef}
          value={html}
          onChange={onHtmlChange}
          placeholder={t('consentTemplatePlaceholder')}
        />
      </div>
      <div>
        <Text strong>{t('consentHrEmailsLabel')}</Text>
        <Input
          className="mt-2"
          disabled={!canEdit}
          value={emails}
          placeholder={t('consentHrEmailsPlaceholder')}
          onChange={(event) => onEmailsChange(event.target.value)}
        />
        <Text type="secondary">{t('consentHrEmailsHelp')}</Text>
      </div>
      <div>
        <Text strong>{t('consentWindowDays', 'Approval window (days)')}</Text>
        <InputNumber
          className="mt-2 block"
          min={1}
          max={31}
          disabled={!canEdit}
          value={windowDays}
          onChange={onWindowDaysChange}
        />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={attachPdfs} disabled={!canEdit} onChange={onAttachPdfsChange} />
        <Text>{t('consentAttachPdfs', 'Attach employee PDFs to the HR email')}</Text>
      </div>
      {canEdit && (
        <Button type="primary" loading={saving} onClick={onSave}>
          {t('saveButton')}
        </Button>
      )}
    </div>
  );
};

export default PayrollConsentTab;
